import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Download, Eye, FolderOpen, Loader2, CheckCircle2, AlertTriangle, Archive,
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeuillesMensuelles({ defaultMois }) {
  const now = new Date();
  const defaultM = defaultMois || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [mois, setMois] = useState(defaultM);
  const [feuilles, setFeuilles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(null);
  const [preview, setPreview] = useState(null); // { url, title }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timesheets/feuilles-mensuelles", { params: { mois } });
      setFeuilles(data.feuilles || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [mois]);
  useEffect(() => { load(); }, [load]);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("oscopge_token")}` });

  const openPreview = async (f) => {
    try {
      const url = `${BASE_URL}/api/timesheets/feuille-mensuelle/${f.salarie_id}/${f.annee}/${f.mois_num}/pdf`;
      const res = await fetch(url, { headers: authHeader() });
      if (!res.ok) throw new Error("Impossible de charger le PDF");
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreview({ url: blobUrl, title: `Feuille ${f.salarie_nom} — ${mois}`, feuille: f });
    } catch (err) { toast.error(err.message); }
  };

  const download = async (f) => {
    try {
      const url = `${BASE_URL}/api/timesheets/feuille-mensuelle/${f.salarie_id}/${f.annee}/${f.mois_num}/pdf`;
      const res = await fetch(url, { headers: authHeader() });
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `FEUILLE-${f.salarie_nom.replace(/\s+/g, "_")}-${mois}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const archiverGed = async (f) => {
    setArchiving(f.salarie_id);
    try {
      const { data } = await api.post(`/timesheets/feuille-mensuelle/${f.salarie_id}/${f.annee}/${f.mois_num}/archiver-ged`);
      toast.success(`Archivé dans la GED (v${data.version})`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Archivage impossible"); }
    finally { setArchiving(null); }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="feuilles-mensuelles">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <FileText className="h-4 w-4 text-[#C5A059]" /> Feuilles de temps mensuelles — PDF
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">Visualisez, téléchargez ou archivez automatiquement dans la GED.</p>
        </div>
        <Input type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-9 w-auto" data-testid="feuilles-mois" />
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#C5A059]" /></div>
      ) : feuilles.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500">
          <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          Aucune feuille de temps saisie pour ce mois.
        </div>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="md:hidden divide-y divide-slate-100">
            {feuilles.map((f) => <FeuilleCard key={f.feuille_id} f={f} archiving={archiving} onPreview={openPreview} onDownload={download} onArchive={archiverGed} />)}
          </div>
          {/* Desktop: table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Salarié</th>
                  <th className="text-right px-4 py-2">Heures</th>
                  <th className="text-center px-4 py-2">Signatures</th>
                  <th className="text-center px-4 py-2">GED</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {feuilles.map((f) => (
                  <tr key={f.feuille_id} className="hover:bg-slate-50" data-testid={`feuille-row-${f.salarie_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{f.salarie_nom}</div>
                      <div className="text-xs text-slate-500">{f.jours_saisis} jours saisis</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-[#A88846]">{f.total_heures.toFixed(2)}h</div>
                      {f.heures_validees > 0 && <div className="text-xs text-emerald-600">{f.heures_validees.toFixed(1)}h validées</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <SignatureIndicator roles={f.signatures} complete={f.complete} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {f.ged_archive ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                          <CheckCircle2 className="h-3 w-3" /> v{f.ged_archive.version}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button size="sm" variant="outline" onClick={() => openPreview(f)} data-testid={`feuille-preview-${f.salarie_id}`} title="Visualiser">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => download(f)} data-testid={`feuille-download-${f.salarie_id}`} title="Télécharger">
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" onClick={() => archiverGed(f)} disabled={archiving === f.salarie_id} className="bg-[#C5A059] hover:bg-[#A88846] text-white" data-testid={`feuille-archive-${f.salarie_id}`} title={f.ged_archive ? "Nouvelle version" : "Archiver dans la GED"}>
                          {archiving === f.salarie_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Preview modal */}
      {preview && (
        <Dialog open={!!preview} onOpenChange={closePreview}>
          <DialogContent className="max-w-5xl h-[90vh] p-0 flex flex-col">
            <DialogHeader className="px-5 pt-4 pb-2 border-b border-slate-200">
              <DialogTitle className="flex items-center justify-between gap-2 pr-6">
                <span className="truncate">{preview.title}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => download(preview.feuille)}><Download className="h-3.5 w-3.5 mr-1" /> Télécharger</Button>
                  <Button size="sm" onClick={() => { archiverGed(preview.feuille); closePreview(); }} className="bg-[#C5A059] hover:bg-[#A88846] text-white"><Archive className="h-3.5 w-3.5 mr-1" /> Archiver GED</Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            <iframe src={preview.url} title="PDF" className="flex-1 w-full border-0" data-testid="feuille-preview-iframe" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SignatureIndicator({ roles, complete }) {
  const need = ["salarie", "employeur", "ge"];
  return (
    <div className="inline-flex items-center gap-1">
      {need.map((r) => {
        const signed = roles.includes(r);
        return (
          <span key={r} title={`${r} ${signed ? "✓" : "en attente"}`} className={`inline-block h-2.5 w-2.5 rounded-full ${signed ? "bg-emerald-500" : "bg-slate-200"}`}></span>
        );
      })}
      {complete && <CheckCircle2 className="h-3 w-3 text-emerald-600 ml-1" />}
      {!complete && roles.length > 0 && <AlertTriangle className="h-3 w-3 text-amber-500 ml-1" />}
    </div>
  );
}

function FeuilleCard({ f, archiving, onPreview, onDownload, onArchive }) {
  return (
    <div className="p-4 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm truncate">{f.salarie_nom}</div>
          <div className="text-xs text-slate-500">{f.jours_saisis} jours · <span className="font-semibold text-[#A88846]">{f.total_heures.toFixed(1)}h</span></div>
        </div>
        <SignatureIndicator roles={f.signatures} complete={f.complete} />
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" className="flex-1" onClick={() => onPreview(f)}><Eye className="h-3 w-3 mr-1" /> Voir</Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={() => onDownload(f)}><Download className="h-3 w-3 mr-1" /> PDF</Button>
        <Button size="sm" className="bg-[#C5A059] hover:bg-[#A88846] text-white flex-1" onClick={() => onArchive(f)} disabled={archiving === f.salarie_id}>
          {archiving === f.salarie_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Archive className="h-3 w-3 mr-1" /> GED</>}
        </Button>
      </div>
      {f.ged_archive && (
        <div className="text-[10px] inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
          <FolderOpen className="h-3 w-3" /> Archivé v{f.ged_archive.version}
        </div>
      )}
    </div>
  );
}
